import { useState, useCallback, useRef } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { useConnection } from '@solana/wallet-adapter-react'
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui'
import { DynamicBondingCurveClient } from '@meteora-ag/dynamic-bonding-curve-sdk'
import { PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js'
import BN from 'bn.js'
import {
  Vault,
  Landmark,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Loader2,
  XCircle,
  History,
  ArrowUpDown,
  Coins,
  GraduationCap,
  Banknote,
  DollarSign,
} from 'lucide-react'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { cn } from '@/lib/utils'
import { truncateAddress, formatDateTime } from '@/lib/utils'
import { useTreasuryData } from '@/hooks/useTreasuryData'
import { useTreasuryFees, type TradingFeePool, type MigrationFeePool } from '@/hooks/useTreasuryFees'
import { useProtocolRevenue } from '@/hooks/useProtocolRevenue'
import { TREASURY_ADDRESS, confirmTransactionWithFallback } from '@/lib/solana'

const DTR_LP_POOL_ADDRESS = '4PaKyQdYi3jvkY88biDDYboPgSqZMTNUb6sPYPB31bJ6'
const DTR_LP_FEE_CLAIMER = 'EdnvRMuipGzXGqVs1mF1p3FDvVCQBHeCpuha2dpFUenC'
import { buildClaimDammV2PositionFeeTransaction, type DammV2PositionFees } from '@/lib/dammV2'
import { supabase } from '@/lib/supabase'
import { fetchDbcConfig } from '@/lib/dbcConfig'
import { useQueryClient } from '@tanstack/react-query'
import { TablePagination } from '@/components/ui/TablePagination'

const TRADING_FEES_PAGE_SIZE = 25
const CLAIM_HISTORY_PAGE_SIZE = 25
const SMALL_TABLE_PAGE_SIZE = 10

const MAX_U64 = new BN('18446744073709551615')

function getSolscanTx(sig: string): string {
  return `https://solscan.io/tx/${sig}`
}

function getSolscanPool(addr: string): string {
  return `https://solscan.io/account/${addr}`
}

interface ClaimState {
  poolAddress: string
  status: 'building' | 'signing' | 'sending' | 'confirming' | 'done' | 'error'
  error?: string
  signature?: string
}

export function TreasuryPage() {
  const { publicKey, sendTransaction, signAllTransactions, connected } = useWallet()
  const { connection } = useConnection()
  const queryClient = useQueryClient()
  const { data: treasury, isLoading: treasuryLoading } = useTreasuryData()
  const { data: fees, isLoading: feesLoading } = useTreasuryFees()
  const { data: revenue, isLoading: revenueLoading } = useProtocolRevenue('all')

  const [claimState, setClaimState] = useState<ClaimState | null>(null)
  const [claimAllRunning, setClaimAllRunning] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null)
  const dammV2SectionRef = useRef<HTMLDivElement>(null)

  // Pagination state
  const [tradingFeesPage, setTradingFeesPage] = useState(0)
  const [migrationPage, setMigrationPage] = useState(0)
  const [autoCollectedPage, setAutoCollectedPage] = useState(0)
  const [dammV2Page, setDammV2Page] = useState(0)
  const [claimHistoryPage, setClaimHistoryPage] = useState(0)

  const isTreasury = publicKey?.toBase58() === TREASURY_ADDRESS.toBase58()
  const isWrongWallet = connected && !isTreasury

  // -- Claim trading fee --
  const claimTradingFee = useCallback(async (pool: TradingFeePool) => {
    if (!publicKey || !isTreasury) return

    setClaimState({ poolAddress: pool.token.poolAddress, status: 'building' })
    try {
      const dbcClient = new DynamicBondingCurveClient(connection, 'confirmed')
      const tx = await dbcClient.partner.claimPartnerTradingFee({
        feeClaimer: publicKey,
        payer: publicKey,
        pool: new PublicKey(pool.token.poolAddress),
        maxBaseAmount: MAX_U64,
        maxQuoteAmount: MAX_U64,
      })

      setClaimState({ poolAddress: pool.token.poolAddress, status: 'signing' })
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
      tx.recentBlockhash = blockhash
      tx.lastValidBlockHeight = lastValidBlockHeight
      tx.feePayer = publicKey

      setClaimState({ poolAddress: pool.token.poolAddress, status: 'sending' })
      const signature = await sendTransaction(tx, connection)

      setClaimState({ poolAddress: pool.token.poolAddress, status: 'confirming', signature })

      const confirmation = await confirmTransactionWithFallback(signature)

      if (confirmation.value.err) {
        throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
      }

      // Log to admin_fee_claims
      await supabase.from('admin_fee_claims').insert({
        pool_address: pool.token.poolAddress,
        token_mint: pool.token.mintAddress,
        claim_type: 'trading_fee',
        amount_lamports_base: '0',
        amount_lamports_quote: String(pool.claimableLamports),
        destination_treasury: TREASURY_ADDRESS.toBase58(),
        signer_wallet: publicKey.toBase58(),
        signature,
        status: 'confirmed',
      })

      setClaimState({ poolAddress: pool.token.poolAddress, status: 'done', signature })
      setToast({ type: 'success', message: `Claimed ${pool.claimableSOL.toFixed(6)} SOL` })
      queryClient.invalidateQueries({ queryKey: ['treasury-fees'] })
      queryClient.invalidateQueries({ queryKey: ['treasury-data'] })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      if (message.includes('User rejected')) {
        setToast({ type: 'info', message: 'Transaction cancelled' })
        setClaimState(null)
      } else {
        setClaimState({ poolAddress: pool.token.poolAddress, status: 'error', error: message })
        setToast({ type: 'error', message: `Claim failed: ${message}` })
      }
    }
  }, [publicKey, isTreasury, connection, sendTransaction, queryClient])

  // -- Claim migration fee --
  const claimMigrationFee = useCallback(async (pool: MigrationFeePool) => {
    if (!publicKey || !isTreasury) return

    setClaimState({ poolAddress: pool.token.poolAddress, status: 'building' })
    try {
      const dbcClient = new DynamicBondingCurveClient(connection, 'confirmed')
      const tx = await dbcClient.partner.partnerWithdrawMigrationFee({
        virtualPool: new PublicKey(pool.token.poolAddress),
        sender: publicKey,
      })

      setClaimState({ poolAddress: pool.token.poolAddress, status: 'signing' })
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
      tx.recentBlockhash = blockhash
      tx.lastValidBlockHeight = lastValidBlockHeight
      tx.feePayer = publicKey

      setClaimState({ poolAddress: pool.token.poolAddress, status: 'sending' })
      const signature = await sendTransaction(tx, connection)

      setClaimState({ poolAddress: pool.token.poolAddress, status: 'confirming', signature })

      const confirmation = await confirmTransactionWithFallback(signature)

      if (confirmation.value.err) {
        throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
      }

      // Fetch migration fee amount from on-chain config for accurate logging
      let migrationFeeLamports = 0
      try {
        const config = await fetchDbcConfig()
        migrationFeeLamports = Math.round(config.migrationFeePerGraduationSOL * 1_000_000_000)
      } catch {
        // Non-blocking: log 0 if config fetch fails, claim still succeeded
      }

      await supabase.from('admin_fee_claims').insert({
        pool_address: pool.token.poolAddress,
        token_mint: pool.token.mintAddress,
        claim_type: 'migration_fee',
        amount_lamports_base: '0',
        amount_lamports_quote: String(migrationFeeLamports),
        destination_treasury: TREASURY_ADDRESS.toBase58(),
        signer_wallet: publicKey.toBase58(),
        signature,
        status: 'confirmed',
      })

      const feeSOL = migrationFeeLamports / 1_000_000_000
      setClaimState({ poolAddress: pool.token.poolAddress, status: 'done', signature })
      setToast({ type: 'success', message: `Migration fee claimed${feeSOL > 0 ? `: ${feeSOL.toFixed(4)} SOL` : ''}` })
      queryClient.invalidateQueries({ queryKey: ['treasury-fees'] })
      queryClient.invalidateQueries({ queryKey: ['treasury-data'] })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      if (message.includes('User rejected')) {
        setToast({ type: 'info', message: 'Transaction cancelled' })
        setClaimState(null)
      } else {
        setClaimState({ poolAddress: pool.token.poolAddress, status: 'error', error: message })
        setToast({ type: 'error', message: `Claim failed: ${message}` })
      }
    }
  }, [publicKey, isTreasury, connection, sendTransaction, queryClient])

  // -- Claim DAMM V2 position fee --
  const claimDammV2Fee = useCallback(async (pos: DammV2PositionFees) => {
    if (!publicKey || !isTreasury) return

    const key = pos.info.dammPoolAddress
    setClaimState({ poolAddress: key, status: 'building' })
    try {
      const tx = await buildClaimDammV2PositionFeeTransaction(
        connection,
        publicKey,
        new PublicKey(pos.info.dammPoolAddress),
        new PublicKey(pos.info.positionAddress),
        new PublicKey(pos.positionNftAccount),
        new PublicKey(pos.tokenAMint),
        new PublicKey(pos.tokenBMint),
        new PublicKey(pos.tokenAVault),
        new PublicKey(pos.tokenBVault),
      )

      setClaimState({ poolAddress: key, status: 'signing' })
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
      tx.recentBlockhash = blockhash
      tx.lastValidBlockHeight = lastValidBlockHeight
      tx.feePayer = publicKey

      setClaimState({ poolAddress: key, status: 'sending' })
      const signature = await sendTransaction(tx, connection)

      setClaimState({ poolAddress: key, status: 'confirming', signature })

      const confirmation = await confirmTransactionWithFallback(signature)

      if (confirmation.value.err) {
        throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
      }

      await supabase.from('admin_fee_claims').insert({
        pool_address: pos.info.dammPoolAddress,
        token_mint: pos.tokenAMint,
        claim_type: 'damm_v2_position_fee',
        amount_lamports_base: String(pos.claimableFeeA),
        amount_lamports_quote: String(pos.claimableFeeB),
        destination_treasury: TREASURY_ADDRESS.toBase58(),
        signer_wallet: publicKey.toBase58(),
        signature,
        status: 'confirmed',
      })

      setClaimState({ poolAddress: key, status: 'done', signature })
      setToast({ type: 'success', message: `Claimed ${pos.claimableFeeBDisplay}` })
      queryClient.invalidateQueries({ queryKey: ['treasury-fees'] })
      queryClient.invalidateQueries({ queryKey: ['treasury-data'] })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      if (message.includes('User rejected')) {
        setToast({ type: 'info', message: 'Transaction cancelled' })
        setClaimState(null)
      } else {
        setClaimState({ poolAddress: key, status: 'error', error: message })
        setToast({ type: 'error', message: `Claim failed: ${message}` })
      }
    }
  }, [publicKey, isTreasury, connection, sendTransaction, queryClient])

  // -- Claim all trading fees: batch sign all, then send sequentially --
  // Uses signAllTransactions so SoulFlare only needs one approval session
  // instead of one per pool. Falls back to sequential signing if unavailable.
  const claimAllTradingFees = useCallback(async () => {
    if (!publicKey || !isTreasury || claimAllRunning) return
    const pools = fees?.tradingFees || []
    if (pools.length === 0) return

    setClaimAllRunning(true)
    let successCount = 0
    let failCount = 0

    // --- Batch sign path (SoulFlare: one approval for all) ---
    if (signAllTransactions) {
      try {
        setToast({ type: 'info', message: `Building ${pools.length} transactions...` })
        const dbcClient = new DynamicBondingCurveClient(connection, 'confirmed')

        // Build all transactions in parallel batches to respect RPC rate limits
        const BATCH_SIZE = 20
        const txPairs: { tx: import('@solana/web3.js').Transaction; pool: TradingFeePool }[] = []
        for (let i = 0; i < pools.length; i += BATCH_SIZE) {
          const batch = pools.slice(i, i + BATCH_SIZE)
          const built = await Promise.all(batch.map(async (pool) => {
            const tx = await dbcClient.partner.claimPartnerTradingFee({
              feeClaimer: publicKey,
              payer: publicKey,
              pool: new PublicKey(pool.token.poolAddress),
              maxBaseAmount: MAX_U64,
              maxQuoteAmount: MAX_U64,
            })
            return { tx, pool }
          }))
          txPairs.push(...built)
        }

        // Get one blockhash for all transactions (valid ~90s, enough for 140 builds)
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
        for (const { tx } of txPairs) {
          tx.recentBlockhash = blockhash
          tx.lastValidBlockHeight = lastValidBlockHeight
          tx.feePayer = publicKey
        }

        // One signing session: SoulFlare shows a single approval for the batch
        setToast({ type: 'info', message: `Approve all ${pools.length} transactions in your wallet...` })
        const signedTxs = await signAllTransactions(txPairs.map(p => p.tx))

        // Send and confirm each signed transaction sequentially
        for (let i = 0; i < signedTxs.length; i++) {
          const pool = txPairs[i].pool
          setClaimState({ poolAddress: pool.token.poolAddress, status: 'sending' })
          try {
            const signature = await connection.sendRawTransaction(signedTxs[i].serialize(), {
              skipPreflight: false,
            })
            setClaimState({ poolAddress: pool.token.poolAddress, status: 'confirming', signature })

            const confirmation = await confirmTransactionWithFallback(signature)
            if (confirmation.value.err) {
              throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
            }

            await supabase.from('admin_fee_claims').insert({
              pool_address: pool.token.poolAddress,
              token_mint: pool.token.mintAddress,
              claim_type: 'trading_fee',
              amount_lamports_base: '0',
              amount_lamports_quote: String(pool.claimableLamports),
              destination_treasury: TREASURY_ADDRESS.toBase58(),
              signer_wallet: publicKey.toBase58(),
              signature,
              status: 'confirmed',
            })

            setClaimState({ poolAddress: pool.token.poolAddress, status: 'done', signature })
            successCount++
          } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown error'
            setClaimState({ poolAddress: pool.token.poolAddress, status: 'error', error: message })
            failCount++
          }
          // Small delay between sends to avoid RPC rate limits
          if (i < signedTxs.length - 1) {
            await new Promise(r => setTimeout(r, 500))
          }
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        if (message.includes('User rejected') || message.includes('rejected')) {
          setToast({ type: 'info', message: 'Batch claim cancelled' })
          setClaimAllRunning(false)
          setClaimState(null)
          return
        }
        // Build or sign failed: surface error and stop
        setToast({ type: 'error', message: `Batch claim failed: ${message}` })
        setClaimAllRunning(false)
        setClaimState(null)
        return
      }
    } else {
      // --- Fallback: sequential sign (one approval per pool) ---
      for (const pool of pools) {
        setClaimState({ poolAddress: pool.token.poolAddress, status: 'building' })
        try {
          const dbcClient = new DynamicBondingCurveClient(connection, 'confirmed')
          const tx = await dbcClient.partner.claimPartnerTradingFee({
            feeClaimer: publicKey,
            payer: publicKey,
            pool: new PublicKey(pool.token.poolAddress),
            maxBaseAmount: MAX_U64,
            maxQuoteAmount: MAX_U64,
          })

          setClaimState({ poolAddress: pool.token.poolAddress, status: 'signing' })
          const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
          tx.recentBlockhash = blockhash
          tx.lastValidBlockHeight = lastValidBlockHeight
          tx.feePayer = publicKey

          setClaimState({ poolAddress: pool.token.poolAddress, status: 'sending' })
          const signature = await sendTransaction(tx, connection)

          setClaimState({ poolAddress: pool.token.poolAddress, status: 'confirming', signature })
          const confirmation = await confirmTransactionWithFallback(signature)

          if (confirmation.value.err) {
            throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
          }

          await supabase.from('admin_fee_claims').insert({
            pool_address: pool.token.poolAddress,
            token_mint: pool.token.mintAddress,
            claim_type: 'trading_fee',
            amount_lamports_base: '0',
            amount_lamports_quote: String(pool.claimableLamports),
            destination_treasury: TREASURY_ADDRESS.toBase58(),
            signer_wallet: publicKey.toBase58(),
            signature,
            status: 'confirmed',
          })

          setClaimState({ poolAddress: pool.token.poolAddress, status: 'done', signature })
          successCount++
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Unknown error'
          if (message.includes('User rejected')) {
            setClaimState(null)
            setClaimAllRunning(false)
            setToast({ type: 'info', message: `Cancelled. Claimed ${successCount} pool${successCount !== 1 ? 's' : ''} before cancellation.` })
            return
          }
          setClaimState({ poolAddress: pool.token.poolAddress, status: 'error', error: message })
          failCount++
        }
        await new Promise(r => setTimeout(r, 1000))
      }
    }

    setClaimAllRunning(false)
    setClaimState(null)
    queryClient.invalidateQueries({ queryKey: ['treasury-fees'] })
    queryClient.invalidateQueries({ queryKey: ['treasury-data'] })
    setToast({
      type: failCount === 0 ? 'success' : 'error',
      message: `Claimed ${successCount}/${pools.length} pools${failCount > 0 ? ` (${failCount} failed)` : ''}`,
    })
  }, [publicKey, isTreasury, claimAllRunning, fees?.tradingFees, connection, sendTransaction, signAllTransactions, queryClient])

  const loading = treasuryLoading || feesLoading
  const tradingFees = fees?.tradingFees || []
  const migrationPools = fees?.migrationPools || []
  const autoCollectedFees = fees?.autoCollectedMigrationFees || []
  const autoCollectedTotalSOL = fees?.autoCollectedTotalSOL || 0
  const dammV2Positions = fees?.dammV2Positions || []
  const claimHistory = fees?.claimHistory || []

  const totalClaimedSOL = claimHistory
    .filter(c => c.status === 'confirmed' || c.status === 'success')
    .reduce((sum, c) => sum + c.amountLamportsQuote / LAMPORTS_PER_SOL, 0)

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold">Treasury</h1>
        <p className="text-sm text-muted-foreground mt-1">Claim protocol fees and manage treasury operations</p>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 max-w-md',
          toast.type === 'success' && 'bg-green-900/90 text-green-100 border border-green-700',
          toast.type === 'error' && 'bg-red-900/90 text-red-100 border border-red-700',
          toast.type === 'info' && 'bg-zinc-800/90 text-zinc-100 border border-zinc-600',
        )}>
          {toast.type === 'success' && <CheckCircle className="h-4 w-4 text-green-400 shrink-0" />}
          {toast.type === 'error' && <XCircle className="h-4 w-4 text-red-400 shrink-0" />}
          <span className="text-sm">{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 text-xs opacity-60 hover:opacity-100">dismiss</button>
        </div>
      )}

      {/* Wallet connection */}
      <div className="rounded-lg border bg-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Vault className="h-5 w-5 text-brand-fg" />
          <div>
            <p className="text-sm font-medium">Treasury Wallet</p>
            <p className="text-xs text-muted-foreground">
              {connected
                ? isTreasury
                  ? `Connected: ${truncateAddress(publicKey!.toBase58(), 6)}`
                  : 'Wrong wallet connected'
                : 'Connect wallet to claim fees'}
            </p>
          </div>
        </div>
        <WalletMultiButton />
      </div>

      {/* Wrong wallet warning */}
      {isWrongWallet && (
        <div className="rounded-lg border border-red-800 bg-red-950/50 p-4 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-300">Wrong wallet connected</p>
            <p className="text-xs text-red-400 mt-1">
              Treasury claims require wallet {truncateAddress(TREASURY_ADDRESS.toBase58(), 6)}.
              Please disconnect and connect the correct wallet.
            </p>
          </div>
        </div>
      )}

      {/* Overview cards */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Treasury Balance"
          value={loading ? '...' : `${treasury?.treasuryBalanceSOL.toFixed(4) || '0.0000'} SOL`}
          icon={Landmark}
          variant="hero"
          loading={loading}
        />
        <StatsCard
          title="Claimable Trading Fees"
          value={loading ? '...' : `${fees?.tradingFeeTotalSOL.toFixed(4) || '0.0000'} SOL`}
          icon={Coins}
          warning={fees && fees.tradingFeePoolCount > 0 ? `${fees.tradingFeePoolCount} pools` : undefined}
          loading={loading}
        />
        <StatsCard
          title="Total Claimed (All-time)"
          value={loading ? '...' : `${totalClaimedSOL.toFixed(4)} SOL`}
          subtitle="Explicit claims only (BC + DAMM V2), not total revenue"
          icon={CheckCircle}
          loading={loading}
        />
        <StatsCard
          title="Graduated Pools"
          value={loading ? '...' : String(migrationPools.length)}
          icon={GraduationCap}
          subtitle={loading ? undefined : `~${autoCollectedTotalSOL.toFixed(2)} SOL auto-collected (already in Dashboard revenue)`}
          loading={loading}
          onClick={() => dammV2SectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
        />
      </div>

      {/* Bonding Curve Trading Fees */}
      <div>
        <div className="flex items-center justify-between">
          <DashboardSectionHeader
            icon={Coins}
            title={`Bonding Curve Trading Fees${fees ? ` (${fees.tradingFeePoolCount} pools)` : ''}`}
          />
          {tradingFees.length > 1 && isTreasury && (
            <button
              onClick={claimAllTradingFees}
              disabled={claimAllRunning || (claimState !== null && claimState.status !== 'error' && claimState.status !== 'done')}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                claimAllRunning
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-brand-fg/20 text-brand-fg hover:bg-brand-fg/30',
              )}
            >
              {claimAllRunning ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Claiming all...
                </span>
              ) : 'Claim All'}
            </button>
          )}
        </div>
        <div className="rounded-lg border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tradingFees.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              No claimable trading fees
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Coin</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Pool</th>
                      <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3">Claimable</th>
                      <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3 w-28">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradingFees.slice(tradingFeesPage * TRADING_FEES_PAGE_SIZE, (tradingFeesPage + 1) * TRADING_FEES_PAGE_SIZE).map((pool) => {
                      const isClaiming = claimState?.poolAddress === pool.token.poolAddress
                      const isDone = isClaiming && claimState.status === 'done'
                      return (
                        <tr key={pool.token.poolAddress} className="border-b border-border/50 last:border-0">
                          <td className="px-4 py-3">
                            <div>
                              <span className="text-sm font-medium">{pool.token.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">{pool.token.symbol}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <a
                              href={getSolscanPool(pool.token.poolAddress)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                            >
                              {truncateAddress(pool.token.poolAddress)}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-mono">{pool.claimableSOL.toFixed(6)} SOL</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isDone ? (
                              <a
                                href={getSolscanTx(claimState.signature!)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-green-400 flex items-center justify-end gap-1"
                              >
                                <CheckCircle className="h-3 w-3" /> Claimed
                              </a>
                            ) : (
                              <button
                                onClick={() => claimTradingFee(pool)}
                                disabled={!isTreasury || (isClaiming && claimState.status !== 'error')}
                                className={cn(
                                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                                  isTreasury
                                    ? 'bg-brand-fg/20 text-brand-fg hover:bg-brand-fg/30'
                                    : 'bg-muted text-muted-foreground cursor-not-allowed',
                                )}
                              >
                                {isClaiming && claimState.status !== 'error' ? (
                                  <span className="flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    {claimState.status === 'building' && 'Building...'}
                                    {claimState.status === 'signing' && 'Sign in wallet'}
                                    {claimState.status === 'sending' && 'Sending...'}
                                    {claimState.status === 'confirming' && 'Confirming...'}
                                  </span>
                                ) : 'Claim'}
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <TablePagination
                page={tradingFeesPage}
                totalItems={tradingFees.length}
                pageSize={TRADING_FEES_PAGE_SIZE}
                onPageChange={setTradingFeesPage}
              />
            </>
          )}
        </div>
      </div>

      {/* Migration Fees */}
      <div>
        <DashboardSectionHeader
          icon={GraduationCap}
          title={`Migration Fees (Partner Withdrawal)${migrationPools.length > 0 ? ` (${migrationPools.length} graduated)` : ''}`}
        />
        <div className="rounded-lg border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : migrationPools.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              No graduated pools
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Coin</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">DBC Pool</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">DAMM V2 Pool</th>
                    <th className="text-center text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                    <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3 w-28">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {migrationPools.slice(migrationPage * SMALL_TABLE_PAGE_SIZE, (migrationPage + 1) * SMALL_TABLE_PAGE_SIZE).map((pool) => {
                    const isClaiming = claimState?.poolAddress === pool.token.poolAddress
                    const isDone = isClaiming && claimState.status === 'done'
                    return (
                      <tr key={pool.token.poolAddress} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-sm font-medium">{pool.token.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">{pool.token.symbol}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={getSolscanPool(pool.token.poolAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          >
                            {truncateAddress(pool.token.poolAddress)}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          {pool.dammPoolAddress ? (
                            <a
                              href={getSolscanPool(pool.dammPoolAddress)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                            >
                              {truncateAddress(pool.dammPoolAddress)}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {pool.partnerWithdrawn ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle className="h-3 w-3" /> Claimed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-yellow-400">
                              <AlertTriangle className="h-3 w-3" /> Claimable
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {pool.partnerWithdrawn ? (
                            <span className="text-xs text-muted-foreground">--</span>
                          ) : isDone ? (
                            <a
                              href={getSolscanTx(claimState.signature!)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-green-400 flex items-center justify-end gap-1"
                            >
                              <CheckCircle className="h-3 w-3" /> Done
                            </a>
                          ) : (
                            <button
                              onClick={() => claimMigrationFee(pool)}
                              disabled={!isTreasury || (isClaiming && claimState.status !== 'error')}
                              className={cn(
                                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                                isTreasury
                                  ? 'bg-brand-fg/20 text-brand-fg hover:bg-brand-fg/30'
                                  : 'bg-muted text-muted-foreground cursor-not-allowed',
                              )}
                            >
                              {isClaiming && claimState.status !== 'error' ? (
                                <span className="flex items-center gap-1">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  {claimState.status === 'building' && 'Building...'}
                                  {claimState.status === 'signing' && 'Sign in wallet'}
                                  {claimState.status === 'sending' && 'Sending...'}
                                  {claimState.status === 'confirming' && 'Confirming...'}
                                </span>
                              ) : 'Claim'}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={migrationPage}
              totalItems={migrationPools.length}
              pageSize={SMALL_TABLE_PAGE_SIZE}
              onPageChange={setMigrationPage}
            />
            </>
          )}
        </div>
      </div>

      {/* Migration Fees (Auto-collected) */}
      <div>
        <DashboardSectionHeader
          icon={Banknote}
          title={`Migration Fees (Auto-collected)${autoCollectedFees.length > 0 ? ` (${autoCollectedFees.length} graduated)` : ''}`}
        />
        <div className="rounded-lg border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : autoCollectedFees.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              No auto-collected migration fees
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border bg-muted/30">
                <span className="text-sm text-muted-foreground">Total auto-collected: </span>
                <span className="text-sm font-mono font-medium">{autoCollectedTotalSOL.toFixed(4)} SOL</span>
                <span className="text-xs text-muted-foreground ml-2">({autoCollectedFees.length} graduation{autoCollectedFees.length !== 1 ? 's' : ''})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Coin</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Graduation Date</th>
                      <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3">Est. Migration Fee</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">TX</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoCollectedFees.slice(autoCollectedPage * SMALL_TABLE_PAGE_SIZE, (autoCollectedPage + 1) * SMALL_TABLE_PAGE_SIZE).map((fee) => (
                      <tr key={fee.token.poolAddress} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-sm font-medium">{fee.token.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">{fee.token.symbol}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-muted-foreground">
                            {fee.migratedAt ? formatDateTime(fee.migratedAt) : '--'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-mono">{fee.estimatedFeeSOL.toFixed(6)} SOL</span>
                        </td>
                        <td className="px-4 py-3">
                          {fee.migrationTx ? (
                            <a
                              href={getSolscanTx(fee.migrationTx)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                            >
                              {truncateAddress(fee.migrationTx)}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <TablePagination
                page={autoCollectedPage}
                totalItems={autoCollectedFees.length}
                pageSize={SMALL_TABLE_PAGE_SIZE}
                onPageChange={setAutoCollectedPage}
              />
            </>
          )}
        </div>
      </div>

      {/* DAMM V2 Position Fees */}
      <div ref={dammV2SectionRef}>
        <DashboardSectionHeader
          icon={ArrowUpDown}
          title={`DAMM V2 Position Fees${dammV2Positions.length > 0 ? ` (${dammV2Positions.length} position${dammV2Positions.length !== 1 ? 's' : ''})` : ''}`}
        />
        <div className="rounded-lg border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : dammV2Positions.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              No DAMM V2 positions
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Coin</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">DAMM V2 Pool</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Position</th>
                    <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3">Claimable SOL</th>
                    <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3 w-28">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dammV2Positions.slice(dammV2Page * SMALL_TABLE_PAGE_SIZE, (dammV2Page + 1) * SMALL_TABLE_PAGE_SIZE).map((pos) => {
                    const isClaiming = claimState?.poolAddress === pos.info.dammPoolAddress
                    const isDone = isClaiming && claimState.status === 'done'
                    const hasClaimable = pos.claimableFeeB > 0 || pos.claimableFeeA > 0
                    return (
                      <tr key={pos.info.positionAddress} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-3">
                          <div>
                            <span className="text-sm font-medium">{pos.info.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">{pos.info.symbol}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={getSolscanPool(pos.info.dammPoolAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          >
                            {truncateAddress(pos.info.dammPoolAddress)}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={getSolscanPool(pos.info.positionAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          >
                            {truncateAddress(pos.info.positionAddress)}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-mono">{pos.claimableFeeBDisplay}</span>
                          {pos.claimableFeeA > 0 && (
                            <div className="text-xs text-muted-foreground">{pos.claimableFeeADisplay}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!hasClaimable ? (
                            <span className="text-xs text-muted-foreground">No fees</span>
                          ) : isDone ? (
                            <a
                              href={getSolscanTx(claimState.signature!)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-green-400 flex items-center justify-end gap-1"
                            >
                              <CheckCircle className="h-3 w-3" /> Claimed
                            </a>
                          ) : (
                            <button
                              onClick={() => claimDammV2Fee(pos)}
                              disabled={!isTreasury || (isClaiming && claimState.status !== 'error')}
                              className={cn(
                                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                                isTreasury
                                  ? 'bg-brand-fg/20 text-brand-fg hover:bg-brand-fg/30'
                                  : 'bg-muted text-muted-foreground cursor-not-allowed',
                              )}
                            >
                              {isClaiming && claimState.status !== 'error' ? (
                                <span className="flex items-center gap-1">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  {claimState.status === 'building' && 'Building...'}
                                  {claimState.status === 'signing' && 'Sign in wallet'}
                                  {claimState.status === 'sending' && 'Sending...'}
                                  {claimState.status === 'confirming' && 'Confirming...'}
                                </span>
                              ) : 'Claim'}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={dammV2Page}
              totalItems={dammV2Positions.length}
              pageSize={SMALL_TABLE_PAGE_SIZE}
              onPageChange={setDammV2Page}
            />
            </>
          )}
        </div>
      </div>

      {/* DTR LP Pool fees */}
      <div>
        <DashboardSectionHeader
          icon={Coins}
          title="DTR LP Pool Fees"
        />
        <div className="rounded-lg border bg-card p-4 space-y-4">
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>Pool</span>
              <a
                href={getSolscanPool(DTR_LP_POOL_ADDRESS)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono hover:text-foreground inline-flex items-center gap-1"
              >
                {truncateAddress(DTR_LP_POOL_ADDRESS, 6)}
                <ExternalLink className="h-3 w-3" />
              </a>
              <span>·</span>
              <span>Fee claimer</span>
              <a
                href={`https://solscan.io/account/${DTR_LP_FEE_CLAIMER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono hover:text-foreground inline-flex items-center gap-1"
              >
                {truncateAddress(DTR_LP_FEE_CLAIMER, 6)}
                <ExternalLink className="h-3 w-3" />
              </a>
              <span>·</span>
              <span>Net of Meteora protocol cut</span>
            </div>
            <div>Separate from launchpad treasury; fees route to the founder wallet (predates auto-fee infrastructure).</div>
          </div>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
            <StatsCard
              title="Claimable Now"
              value={revenueLoading ? '...' : `${(revenue?.lpFeesClaimableSOL ?? 0).toFixed(6)} SOL`}
              subtitle={revenue?.lpFeesPositionCount
                ? `${revenue.lpFeesPositionCount} position${revenue.lpFeesPositionCount === 1 ? '' : 's'}`
                : 'No positions detected'}
              icon={Coins}
              loading={revenueLoading}
            />
            <StatsCard
              title="Claimed Lifetime"
              value={revenueLoading ? '...' : `${(revenue?.lpFeesClaimedSOL ?? 0).toFixed(4)} SOL`}
              subtitle="From admin_fee_claims (dtr_lp_fee)"
              icon={CheckCircle}
              loading={revenueLoading}
            />
            <StatsCard
              title="Total LP Revenue"
              value={revenueLoading ? '...' : `${(revenue?.lpFeesSOL ?? 0).toFixed(4)} SOL`}
              subtitle="Claimable + Claimed (lifetime)"
              icon={DollarSign}
              loading={revenueLoading}
            />
          </div>
          {revenue?.lpFeesSkipped && (
            <div className="text-xs text-yellow-400 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              On-chain claimable read failed; numbers reflect last successful claims-only sum.
            </div>
          )}
        </div>
      </div>

      {/* Claim History */}
      <div>
        <DashboardSectionHeader
          icon={History}
          title="Claim History"
        />
        <div className="rounded-lg border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : claimHistory.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              No claim history
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Date</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Pool</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Type</th>
                    <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3">Amount</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">TX</th>
                    <th className="text-center text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {claimHistory.slice(claimHistoryPage * CLAIM_HISTORY_PAGE_SIZE, (claimHistoryPage + 1) * CLAIM_HISTORY_PAGE_SIZE).map((claim) => (
                    <tr key={claim.id} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {formatDateTime(claim.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={getSolscanPool(claim.poolAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                        >
                          {truncateAddress(claim.poolAddress)}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded-full',
                          claim.claimType === 'trading_fee' && 'bg-blue-500/10 text-blue-400',
                          claim.claimType === 'migration_fee' && 'bg-purple-500/10 text-purple-400',
                          claim.claimType === 'damm_v2_position_fee' && 'bg-emerald-500/10 text-emerald-400',
                          claim.claimType === 'partner' && 'bg-blue-500/10 text-blue-400',
                        )}>
                          {claim.claimType.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-mono">
                          {(claim.amountLamportsQuote / LAMPORTS_PER_SOL).toFixed(6)} SOL
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {claim.signature ? (
                          <a
                            href={getSolscanTx(claim.signature)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          >
                            {truncateAddress(claim.signature)}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(claim.status === 'confirmed' || claim.status === 'success') ? (
                          <span className="text-xs text-green-400">confirmed</span>
                        ) : claim.status === 'failed' || claim.status === 'sim_failed' ? (
                          <span className="text-xs text-red-400">{claim.status}</span>
                        ) : (
                          <span className="text-xs text-yellow-400">{claim.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={claimHistoryPage}
              totalItems={claimHistory.length}
              pageSize={CLAIM_HISTORY_PAGE_SIZE}
              onPageChange={setClaimHistoryPage}
            />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
